import { useCallback, useMemo, useContext, Context } from "react"
import { GridInitialState, GridColumnDimensions } from "@mui/x-data-grid-premium"

type DatagridState = Record<string, GridInitialState> & {
  shared: {
    labelDimensions?: GridColumnDimensions
  }
}

interface DataGridContext {
  datagridState: DatagridState
  setDatagridState: (state: DatagridState) => void
}

interface UseContextPersistenceReturn {
  isContextAvailable: boolean
  gridState: GridInitialState | null
  saveGridStateToContext: (state: GridInitialState) => boolean
}

export function useContextPersistence(
  gridId: string,
  contextToUse: Context<DataGridContext>,
): UseContextPersistenceReturn {
  const context = useContext(contextToUse)

  const gridState = useMemo(() => {
    const currentDatagridState = context.datagridState
    if (!currentDatagridState) {
      return null
    }
    const baseState = currentDatagridState[gridId]
    if (!baseState) {
      return null
    }
    const shared = currentDatagridState.shared

    if (!shared) {
      return baseState
    }

    const currentDimensions = baseState.columns?.dimensions || {}
    const mergedDimensions = { ...currentDimensions }
    if (shared.labelDimensions !== undefined) {
      mergedDimensions.label = shared.labelDimensions
    }
    return {
      ...baseState,
      columns: {
        ...baseState.columns,
        dimensions: mergedDimensions,
      },
    }
  }, [context.datagridState, gridId])

  const saveGridStateToContext = useCallback((state: GridInitialState) => {
    if (!context.setDatagridState) {
      return false
    }
    const currentDatagridState = context.datagridState || {}

    const newDatagridState: DatagridState = { ...currentDatagridState, [gridId]: state }
    const labelDimensions = state.columns?.dimensions?.label
    if (labelDimensions) {
      newDatagridState.shared = {
        labelDimensions,
      }
    }
    context.setDatagridState(newDatagridState)
    return true
  }, [context, gridId])

  return {
    isContextAvailable: !!context.setDatagridState,
    gridState,
    saveGridStateToContext,
  }
}
